/**
 * TraceInput — end-of-round screen.
 *
 * 2026-05-21 v3 redesign:
 *   - Removed the "你停在哪" free-text field. Without an LLM follow-up it asked
 *     users to write into a void; users said it felt directionless.
 *   - Removed the per-round LLM-reflection opt-in (the 4-field experience screen).
 *     Reflection is now reserved for the segment-level recap.
 *   - Three actions only, all neutral phrasing:
 *       1. 完成了      → done, card archives to the desk drawer
 *       2. 继续做      → in_progress, card stays in the work area for next round
 *       3. 调整策略    → reframe — opens StuckReframe inline; on accepted action,
 *                       commits the round (in_progress) and updates the card action
 *   - "撕了" (drop) is no longer reachable from here. Tearing is desk-only,
 *     via the hover button on a card.
 */
import { useState } from 'react';
import type { UserRoundStatus, RoundExperience } from '../types';
import type { PoolCard } from '../state/cardPool';
import { StuckReframe } from './StuckReframe';

interface TraceInputProps {
  card: PoolCard;
  onCompleted: (
    status: UserRoundStatus,
    returnPoint: string,
    experience: RoundExperience,
    actionUsedOverride?: string,
    // W6: true 表示用户想接着做同一张卡 → 宿主直接重开一轮计时,不回工作台重选。
    // 「继续做」和「调整策略-接受新行动」是 true;「完成」「放一放」是 false。
    continueSameCard?: boolean,
  ) => void;
  /** Called when the user reframes the card inside this screen (rewrite/clarify
   * happy path). Mirrors ThreeCards's onUpdateAction. */
  onActionChanged: (cardId: string, newAction: string) => void;
}

function buildExperience(status: UserRoundStatus): RoundExperience {
  return {
    trace: '完成了一轮 5 分钟。',
    stuckPoint: null,
    nextStart:
      status === 'done'
        ? '这张已完成,可以在归档里回看。'
        : '下次接着继续。',
    support: '这一轮记下了。',
  };
}

type Mode = 'choose' | 'reframe';

export function TraceInput({ card, onCompleted, onActionChanged }: TraceInputProps) {
  const [mode, setMode] = useState<Mode>('choose');

  if (mode === 'reframe') {
    return (
      <StuckReframe
        card={card}
        onAccepted={(newAction) => {
          // 1) commit the action change to the card pool + segment.cards
          onActionChanged(card.id, newAction);
          // 2) finish this round as in_progress so the segment advances — and reopen
          //    the timer on this same card with the new action (W6: continue is 1 tap).
          onCompleted('in_progress', '', buildExperience('in_progress'), newAction, true);
        }}
        onPutAside={() => {
          // User accepted the graceful exit — card stays in pool, round advances,
          // but we do NOT reopen the timer on it (they're stepping away) → back to desk.
          onCompleted('in_progress', '', buildExperience('in_progress'));
        }}
        onCancel={() => setMode('choose')}
      />
    );
  }

  return (
    <section className="panel wish trace-input">
      <span className="tiny-label">
        <i className="dot cyan" /> 这一轮停在这里了
      </span>
      <h1>「{card.title}」</h1>
      <div className="strategy-row">
        <span className="tiny muted">行动:</span>
        <p className="strategy-current">{card.action}</p>
      </div>

      <span className="tiny-label" style={{ marginTop: '14px' }}>
        <i className="dot amber" /> 这张卡现在怎么处理
      </span>
      <ul className="status-list">
        <li>
          <button
            type="button"
            className="status-option"
            onClick={() => onCompleted('done', '', buildExperience('done'))}
          >
            <strong>✓ 完成了</strong>
            <span className="muted tiny">这张卡可以归档,不会再出现。</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            className="status-option"
            onClick={() => onCompleted('in_progress', '', buildExperience('in_progress'), undefined, true)}
          >
            <strong>→ 继续做</strong>
            <span className="muted tiny">这张卡留在工作区,马上接着开下一轮 5 分钟。</span>
          </button>
        </li>
        <li>
          <button
            type="button"
            className="status-option"
            onClick={() => setMode('reframe')}
          >
            <strong>✎ 调整策略</strong>
            <span className="muted tiny">改一下这张卡的下一步,再继续。</span>
          </button>
        </li>
      </ul>
    </section>
  );
}
